import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Image as ImageIcon, FileCode2, AlertCircle, Download, Printer } from "lucide-react";
import { loadQRCode } from "@/lib/qrcode-lazy";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, breadcrumbJsonLd, softwareAppJsonLd, ldScript } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";

const PATH = "/qr-code-generator";
const TITLE = "Online QR Code Generator — Create QR Codes (PNG / SVG)";
const DESC =
  "Free online QR code generator. Choose error correction, size, margin and colors, then download your QR code as a PNG or SVG — all rendered in your browser.";

const FAQ = [
  {
    q: "What Can I Encode in a QR Code?",
    a: "Anything that fits as text: a URL, plain text, Wi-Fi credentials (WIFI:T:WPA;S:MySSID;P:mypassword;;), a vCard, an email link (mailto:you@example.com) or an SMS (sms:+15551234567). The generator encodes the literal string you enter.",
  },
  {
    q: "Which Error Correction Level Should I Use?",
    a: "L (~7%) for clean digital use, M (~15%) is the default and a good general choice, Q (~25%) for printed labels that may get scuffed, and H (~30%) if you plan to overlay a logo or expect heavy damage. Higher levels make the QR denser.",
  },
  {
    q: "Is My Data Uploaded Anywhere?",
    a: "No. Generation happens fully in your browser using the qrcode library. The value you type and the PNG/SVG you download never touch our servers.",
  },
  {
    q: "PNG or SVG — Which Should I Download?",
    a: "Use PNG for screens, slides and email. Use SVG for print and packaging — it stays sharp at any size and produces the smallest, cleanest files for vector design tools.",
  },
  {
    q: "Will Custom Colors Still Scan?",
    a: "Yes, as long as there is strong contrast between the dark modules and the background. Keep the background light and the modules dark, and avoid inverting them — many scanners reject light-on-dark codes.",
  },
  {
    q: "Can I Generate a QR Code for Free?",
    a: "Yes. This generator is free, needs no account and puts no watermark or branding on the PNG, SVG or PDF you save. Because the code is static, it also never stops working when a subscription ends.",
  },
  {
    q: "How Many QR Codes Can Be Generated?",
    a: "There is no limit on this page: change the text and a new code is drawn, then download it and start the next one. Codes are made one at a time, so for a long list it is quicker to keep the settings fixed and only replace the text between downloads.",
  },
  {
    q: "Are QR Code Generators Safe?",
    a: "This one is safe for your data, because the code is drawn in your browser and the text you enter is never sent to a server. With services that make dynamic codes, the provider controls where the code redirects, so the destination can change or disappear later. Whatever generator you use, scan the finished code yourself to confirm it opens the page you expect.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "Online QR Code Generator", path: PATH },
];

type EcLevel = "L" | "M" | "Q" | "H";

const MAX_QR_CHARS = 2000;
const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function validateQrInputs(value: string, darkColor: string, lightColor: string): string | null {
  if (value.length === 0) return "Enter some text or a URL to encode.";
  // qrcode counts UTF-8 bytes, but characters is a close, friendly upper bound
  if (value.length > MAX_QR_CHARS)
    return `QR payload is too long (${value.length} characters). Keep it under ${MAX_QR_CHARS}.`;
  if (!HEX_COLOR.test(darkColor))
    return "Foreground colour must be a hex value like #0f172a or #000.";
  if (!HEX_COLOR.test(lightColor))
    return "Background colour must be a hex value like #ffffff or #fff.";
  if (darkColor.toLowerCase() === lightColor.toLowerCase())
    return "Foreground and background colours must differ.";
  return null;
}

function safeFilename(value: string): string {
  const slug = value.replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "qrcode";
  return `qrcode-${slug}`;
}

function QrCodeGeneratorPage() {
  const [value, setValue] = useState("https://readbarcode.com");
  const [ecLevel, setEcLevel] = useState<EcLevel>("M");
  const [size, setSize] = useState(320);
  const [margin, setMargin] = useState(2);
  const [darkColor, setDarkColor] = useState("#0f172a");
  const [lightColor, setLightColor] = useState("#ffffff");
  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const opts = useMemo(
    () => ({
      errorCorrectionLevel: ecLevel,
      margin,
      width: size,
      color: { dark: darkColor, light: lightColor },
    }),
    [ecLevel, margin, size, darkColor, lightColor],
  );

  useEffect(() => {
    const validationErr = validateQrInputs(value, darkColor, lightColor);
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const QRCode = await loadQRCode();
        if (canvasRef.current) {
          await QRCode.toCanvas(canvasRef.current, value, opts);
        }
        const svg = await QRCode.toString(value, { ...opts, type: "svg" });
        if (cancelled) return;
        setSvgMarkup(svg);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to render QR code.";
        setError(
          msg.toLowerCase().includes("too big")
            ? "QR payload is too long for the selected error correction. Shorten it or lower the error correction level."
            : msg,
        );
        setSvgMarkup("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, opts, darkColor, lightColor]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(value)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(value)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [printOpen, setPrintOpen] = useState(false);

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Online QR Code Generator"
      description="Create a QR code with custom error correction, size, margin and colors. Download as PNG or SVG. Everything runs in your browser. The Print button lays the code out as a single label or a grid of copies with a caption, then prints it or saves it as a vector PDF."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the online QR code generator"
            about={
              <>
                <p>
                  The ReadBarcode online QR code generator turns any text — a URL, Wi-Fi credentials,
                  contact card, plain message — into a scannable QR code right in your browser. Pick an
                  error correction level, set the size, tweak the quiet zone and colors, and download
                  the result as a PNG for screens or an SVG for print.
                </p>
                <p>
                  Encoding runs entirely on your device with the open-source qrcode library — no
                  account, no upload, no tracking pixel. The QR code you generate is yours, and we never
                  see it.
                </p>
              </>
            }
            whenToUse={[
              "Sharing a URL on a poster, business card or product packaging.",
              "Adding a Wi-Fi join QR to the wall of a café or rental.",
              "Linking a printed menu, leaflet or signage to its online version.",
              "Encoding a vCard so people can save your contact in one scan.",
              "Generating a deep-link QR for an app onboarding flow.",
              "Producing a quick QR for a payment link, mailto: or sms: action.",
            ]}
            howItWorks={[
              {
                title: "Enter the data",
                desc: "Paste a URL or type the text, Wi-Fi string, mailto: or sms: link you want to encode.",
              },
              {
                title: "Pick error correction",
                desc: "L for clean digital use, M as a default, Q or H if it'll be printed or overlaid with a logo.",
              },
              {
                title: "Choose size and margin",
                desc: "Bigger is friendlier for distant scans. Keep at least 4 modules of quiet zone.",
              },
              {
                title: "Set the colors",
                desc: "Pick a dark foreground and a light background — strong contrast keeps it scannable.",
              },
              {
                title: "Download PNG or SVG",
                desc: "PNG for screens and slides. SVG for print, packaging and design tools.",
              },
            ]}
            whatToAvoid={[
              "Inverting the colors (light on dark) — many scanners reject inverted QR codes.",
              "Using low contrast colors — pastel-on-white often fails on mid-range phone cameras.",
              "Removing the quiet zone (margin: 0) — scanners need a clear border around the symbol.",
              "Cramming too much text into one QR — long URLs make it dense and hard to scan.",
              "Resizing a downloaded PNG up in a design tool — re-generate at the target size instead.",
            ]}
            tips={[
              "If you'll overlay a logo, use H error correction and keep the logo under ~20% of the area.",
              "Test the QR with a few different phones before printing it on packaging.",
              "For URLs, shorten them first — fewer characters means a less dense, more reliable code.",
              "Always download SVG for print artwork — it stays crisp at any DPI.",
              "Use the Barcode Reader on this site to verify the QR decodes correctly.",
            ]}
          />
          <PrivacyNote />
          <h2>Free QR Code Generator With No Expiration</h2>
          <p>QR codes made on this page never expire, because the text or URL is stored in the pattern itself, not on a server. The code works for as long as the print survives.</p>
          <p>Codes that stop working are usually dynamic codes: they hold a short link on a provider's domain, which stops redirecting when the subscription ends.</p>
          <p>The trade-off is that a static code cannot be edited once printed. If the destination may change, encode a URL you control and update that page instead.</p>
          <p>This free online QR code generator needs no sign-up and adds no watermark. As a custom QR code generator it sets colors, size, margin and error correction, but it does not add a logo.</p>
          <h2>Make a QR Code From a URL</h2>
          <p>To make a QR code from a URL, paste the full address, including https://, into the Text or URL box. The preview updates as you type and is ready to download.</p>
          <ol>
            <li>Paste the address into Text or URL, replacing the sample.</li>
            <li>Pick M error correction for most links, or Q or H for print that may get scuffed.</li>
            <li>Set the size, and raise the quiet zone to 4 modules if the code will be printed.</li>
            <li>Scan the preview with your phone, then click PNG, SVG or Print.</li>
          </ol>
          <p>Some payloads follow a strict format: the <Link to="/wifi-qr-code-generator">Wi-Fi QR code generator</Link> and the <Link to="/vcard-qr-generator">vCard QR code generator</Link> build them from form fields, and the <Link to="/gs1-digital-link-generator">GS1 Digital Link generator</Link> builds a product URL carrying a GTIN.</p>
          <h3>Turn a Link Into a Scannable Code</h3>
          <p>Keep the https:// prefix so every scanner treats the text as a web address rather than plain text.</p>
          <p>Every character adds modules, so a shorter link scans from further away. Drop tracking parameters you do not need before generating.</p>
          <p>Used as a QR code generator from URL text, this tool accepts up to 2,000 characters. The QR Code standard defines 40 symbol versions, from 21 × 21 to 177 × 177 modules.</p>
          <p>Before printing, scan the preview with the <Link to="/qr-code-reader">QR code reader</Link> on another device to confirm the link opens.</p>
          <h2>PNG or SVG: Which QR Code Download to Use</h2>
          <p>Download a PNG for screens and email, and an SVG for anything that will be printed or resized. Both hold the same code and settings.</p>
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">PNG</th>
                  <th scope="col">SVG</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">File type</th>
                  <td>Raster image made of pixels</td>
                  <td>Vector image made of shapes</td>
                </tr>
                <tr>
                  <th scope="row">Size</th>
                  <td>Fixed by the Size slider, 128 to 1024 px</td>
                  <td>Scales to any size without blurring</td>
                </tr>
                <tr>
                  <th scope="row">Best for</th>
                  <td>Websites, slides, chat apps and email</td>
                  <td>Print, packaging, signs and design tools</td>
                </tr>
              </tbody>
            </table>
          </TableScroll>
          <p>A PNG blurs when stretched beyond its pixel size. For a larger PNG, move the Size slider and download again rather than enlarging the file.</p>
          <p>Without design software, the Print button places the code on A4 or US Letter paper, as one label or a grid of copies with a caption, and can save a vector PDF.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="value">Text or URL</Label>
              <Textarea
                id="value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="https://example.com"
                rows={4}
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Any text: URL, plain text, Wi-Fi string, vCard, mailto: or sms: link.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ec">Error correction</Label>
              <Select value={ecLevel} onValueChange={(v) => setEcLevel(v as EcLevel)}>
                <SelectTrigger id="ec">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">L — ~7% recovery (smallest)</SelectItem>
                  <SelectItem value="M">M — ~15% recovery (default)</SelectItem>
                  <SelectItem value="Q">Q — ~25% recovery</SelectItem>
                  <SelectItem value="H">H — ~30% recovery (logo overlays)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Size</Label>
                <span className="text-xs text-muted-foreground">{size}px</span>
              </div>
              <Slider
                min={128}
                max={1024}
                step={16}
                value={[size]}
                onValueChange={(v) => setSize(v[0])}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Quiet zone (margin)</Label>
                <span className="text-xs text-muted-foreground">{margin} modules</span>
              </div>
              <Slider
                min={0}
                max={8}
                step={1}
                value={[margin]}
                onValueChange={(v) => setMargin(v[0])}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dark">Foreground</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="dark"
                    type="color"
                    value={darkColor}
                    onChange={(e) => setDarkColor(e.target.value)}
                    className="h-9 w-12 cursor-pointer p-1"
                  />
                  <Input
                    value={darkColor}
                    onChange={(e) => setDarkColor(e.target.value)}
                    className="font-mono text-xs"
                    aria-label="Foreground hex"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="light">Background</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="light"
                    type="color"
                    value={lightColor}
                    onChange={(e) => setLightColor(e.target.value)}
                    className="h-9 w-12 cursor-pointer p-1"
                  />
                  <Input
                    value={lightColor}
                    onChange={(e) => setLightColor(e.target.value)}
                    className="font-mono text-xs"
                    aria-label="Background hex"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div
              className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border p-4"
              style={{ backgroundColor: lightColor }}
            >
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated QR code preview"
                />
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button onClick={downloadPng} disabled={!!error}>
                <ImageIcon className="h-4 w-4" aria-hidden />
                PNG
              </Button>
              <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG
              </Button>
              <Button
                variant="outline"
                onClick={() => setPrintOpen(true)}
                disabled={!!error || !svgMarkup}
              >
                <Printer className="h-4 w-4" aria-hidden />
                Print
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <SupportedContent />
      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`QR Code · ${value.slice(0, 60) || "label"}`}
        filename={safeFilename(value)}
        svgMarkup={svgMarkup}
        defaultCaption={value}
        defaults={{
          layoutKind: "single",
          widthMm: 50,
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function SupportedContent() {
  const items = ["URL", "Plain text", "Wi-Fi", "vCard", "mailto:", "sms:", "tel:", "geo:"];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Common QR Payloads</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        QR codes are just text — these are the formats most scanners recognise as actionable.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {items.map((f) => (
          <Badge key={f} variant="outline" className="px-3 py-1 text-sm">
            {f}
          </Badge>
        ))}
      </div>
    </section>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        QR codes are rendered locally in your browser with the open-source qrcode library. The
        value you enter and the PNG or SVG you download never touch our servers.
      </p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Scan a QR code you just generated to verify it decodes correctly.",
    },
    {
      path: "/wifi-qr-code-generator",
      label: "Wi-Fi QR Code Generator",
      desc: "Build a QR code that lets guests join your Wi-Fi network in one scan.",
    },
    {
      path: "/vcard-qr-generator",
      label: "vCard QR Code Generator",
      desc: "Turn contact details into a QR code that saves to the address book.",
    },
    {
      path: "/gs1-digital-link-generator",
      label: "GS1 Digital Link Generator",
      desc: "Build a GS1 Digital Link URL from a GTIN and render it as a QR code.",
    },
    {
      path: "/barcode-generator",
      label: "Barcode Generator",
      desc: "Generate UPC, EAN, Code 128 and more as PNG or SVG.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Decode any 1D or 2D barcode from camera or image.",
    },
    {
      path: "/barcode-guides/1d-vs-2d-barcodes",
      label: "1D vs 2D barcodes",
      desc: "When QR makes sense and when a UPC or Code 128 is the right call.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode formats guide",
      desc: "Field guide to common 1D and 2D barcode symbologies.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/qr-code-generator")({
  component: QrCodeGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Online QR Code Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
